import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import HomeLayout from "../../asset/layout/HomeLayout";
import SearchBar from "../../components/mui/SearchBar";
import { useDispatch } from "react-redux";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/mui/CustomButton";
import ResponsiveDatePickers from "../../components/mui/PichDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getClientByToken } from "../../actions/clientsAuth";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const columns = [
  "Client",
  "Email",
  "GST Status",
  "Company",
  "GST Number",
  "Type",
  "CIN",
  "Company PAN",
  "Trade Name",
  "Director PAN",
  "Director Aadhaar",
  "Office Address",
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: "100%" }}>
    <Box sx={{ p: 2.5, position: "relative", overflow: "hidden" }}>
      <Box
        sx={{
          position: "absolute",
          top: -30,
          right: -30,
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(accent, 0.2)} 0%, transparent 70%)`,
        }}
      />
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.75rem",
              color: "#0f172a",
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const GstStatusChip = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const isActive =
    s.includes("active") || s.includes("registered") || s.includes("verified");
  const color = isActive ? "#16a34a" : status ? "#d97706" : "#64748b";
  return (
    <Chip
      label={status || "—"}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        color,
        bgcolor: alpha(color, 0.12),
        border: `1px solid ${alpha(color, 0.2)}`,
        textTransform: "capitalize",
      }}
    />
  );
};

const display = (val) => (val != null && val !== "" ? val : "—");

const getClientName = (row) => {
  const parts = [row?.firstName, row?.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : row?.companyName || row?.email || "—";
};

const getInitials = (row) => {
  const name = getClientName(row);
  return name.charAt(0).toUpperCase();
};

export const ClientManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [allUserData, setAllUserData] = useState([]);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
    }
  }, [navigate]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const getData = () => {
    setLoading(true);
    dispatch(
      getClientByToken(
        (data) => {
          const filteredData = data.data.data.filter((item) => {
            const matchQuery =
              item?.firstName
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.lastName
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.phone?.includes(searchTerm) ||
              item?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
              item?.companyName
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.companyType
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.cinNumber
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.officeAddress
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.directorAdhaarNumber
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.directorPanCardNumber
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.tradeName
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.companyPanCardNumber
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.gst?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
              item?.gstStatus
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase());

            const matchDateRange =
              (!selectedDateFrom ||
                new Date(item.updatedAt) >= new Date(selectedDateFrom)) &&
              (!selectedDateTo ||
                new Date(item.updatedAt) <= new Date(selectedDateTo));

            return matchQuery && matchDateRange;
          });

          filteredData.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          dispatch({ type: "ALL_USER", payload: filteredData });
          setAllUserData(filteredData);
          setLoading(false);
        },
        () => {
          setLoading(false);
        }
      )
    );
  };

  useEffect(() => {
    getData();
  }, [searchTerm, countPerPage, currentPage, selectedDateFrom, selectedDateTo]);

  const stats = useMemo(() => {
    const total = allUserData.length;
    const withCompany = allUserData.filter((c) => c?.companyName).length;
    const withGst = allUserData.filter((c) => c?.gst || c?.gstStatus).length;
    const complete = allUserData.filter(
      (c) => c?.cinNumber && c?.companyPanCardNumber && c?.officeAddress
    ).length;
    return { total, withCompany, withGst, complete };
  }, [allUserData]);

  const handleSearchClear = () => {
    setSearchTerm("");
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setCurrentPage(1);
  };

  const paginatedData = allUserData.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  const totalPages = Math.ceil(allUserData.length / countPerPage) || 1;

  const handleExport = () => {
    const header = [
      "Name",
      "Email",
      "GST Status",
      "Company Name",
      "GST Number",
      "Company Type",
      "CIN Number",
      "Company PAN",
      "Trade Name",
      "Director PAN",
      "Director Aadhaar",
      "Office Address",
    ];

    const dataToExport = allUserData.map((row) => [
      getClientName(row),
      row?.email,
      row?.gstStatus,
      row?.companyName,
      row?.gst,
      row?.companyType,
      row?.cinNumber,
      row?.companyPanCardNumber,
      row?.tradeName,
      row?.directorPanCardNumber,
      row?.directorAdhaarNumber,
      row?.officeAddress,
    ]);

    dataToExport.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(blob, "Clients.xlsx");
  };

  const handleClientNameClick = (id) => {
    navigate(`/client-detail/${id}`);
  };

  const premiumButtonSx = {
    height: "44px",
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(135deg, #11409B 0%, #2563eb 100%)",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #0f3580 0%, #1d4ed8 100%)",
    },
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)",
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: "24px",
              background:
                "linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(17, 64, 155, 0.35)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -30,
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: alpha("#fff", 0.06),
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <GroupsOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: "0.1em", opacity: 0.85 }}
                  >
                    CLIENT MANAGEMENT
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  Clients dashboard
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  Browse B2B clients, company details, and GST records. Click a
                  client name to open their full profile.
                </Typography>
              </Box>
              <CustomButton
                variant="contained"
                onClick={handleExport}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  ...premiumButtonSx,
                  bgcolor: alpha("#fff", 0.15),
                  backdropFilter: "blur(8px)",
                  border: `1px solid ${alpha("#fff", 0.25)}`,
                  boxShadow: "none",
                  "&:hover": { bgcolor: alpha("#fff", 0.22) },
                }}
              >
                Export clients
              </CustomButton>
            </Stack>
          </Box>
        </motion.div>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={GroupsOutlinedIcon}
                label="Total clients"
                value={stats.total}
                accent="#4f46e5"
                gradient="linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={BusinessOutlinedIcon}
                label="With company"
                value={stats.withCompany}
                accent="#2563eb"
                gradient="linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={ReceiptLongOutlinedIcon}
                label="GST on file"
                value={stats.withGst}
                accent="#059669"
                gradient="linear-gradient(135deg, #34d399 0%, #059669 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={VerifiedOutlinedIcon}
                label="Profile complete"
                value={stats.complete}
                accent="#7c3aed"
                gradient="linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)"
              />
            </motion.div>
          </Grid>
        </Grid>

        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: "#11409B", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                Filters
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateFrom}
                  setSelectedDate={setSelectedDateFrom}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateTo}
                  setSelectedDate={setSelectedDateTo}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: "2.75rem",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha("#11409B", 0.25),
                    color: "#11409B",
                    "&:hover": {
                      borderColor: "#11409B",
                      bgcolor: alpha("#11409B", 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: "#11409B" }} />
                <Typography sx={{ mt: 2, color: "#64748b" }}>
                  Loading clients…
                </Typography>
              </Stack>
            </PanelCard>
          ) : paginatedData.length > 0 ? (
            <TableContainer
              component={PanelCard}
              sx={{ overflowX: "auto" }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha("#11409B", 0.04),
                      "& th": {
                        borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "#11409B",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          py: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row) => (
                    <TableRow
                      key={row._id || row.id || row.email}
                      sx={{
                        transition: "background 0.2s",
                        "&:hover": { bgcolor: alpha("#11409B", 0.03) },
                        "& td": {
                          borderBottom: `1px solid ${alpha("#11409B", 0.06)}`,
                          py: 1.75,
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          onClick={() => handleClientNameClick(row?._id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              background:
                                "linear-gradient(135deg, #11409B, #2563eb)",
                              fontWeight: 700,
                              fontSize: "1rem",
                              border: "2px solid #fff",
                              boxShadow: "0 4px 12px rgba(17,64,155,0.2)",
                            }}
                          >
                            {getInitials(row)}
                          </Avatar>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#2563eb",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {getClientName(row)}
                            </Typography>
                            {row?.phone && (
                              <Typography variant="caption" sx={{ color: "#64748b" }}>
                                {row.phone}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#475569" }}>
                          {display(row?.email)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <GstStatusChip status={row?.gstStatus} />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#0f172a", maxWidth: 180 }}
                        >
                          {display(row?.companyName)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", color: "#475569" }}
                        >
                          {display(row?.gst)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={display(row?.companyType)}
                          size="small"
                          sx={{
                            bgcolor: alpha("#11409B", 0.08),
                            color: "#11409B",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#475569" }}>
                          {display(row?.cinNumber)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", color: "#475569" }}
                        >
                          {display(row?.companyPanCardNumber)}
                        </Typography>
                      </TableCell>
                      <TableCell>{display(row?.tradeName)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", color: "#475569" }}
                        >
                          {display(row?.directorPanCardNumber)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", color: "#475569" }}
                        >
                          {display(row?.directorAdhaarNumber)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            maxWidth: 220,
                            whiteSpace: "normal",
                          }}
                        >
                          {display(row?.officeAddress)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <GroupsOutlinedIcon
                  sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  No clients found
                </Typography>
                <Typography sx={{ color: "#64748b", mt: 1, textAlign: "center" }}>
                  Try adjusting your search or date filters.
                </Typography>
              </Stack>
            </PanelCard>
          )}
        </motion.div>

        {!loading && allUserData.length > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, allUserData.length)} of{" "}
                  {allUserData.length}
                </Typography>

                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha("#11409B", 0.2)}`,
                      borderRadius: "10px",
                      px: 1.5,
                      py: 0.75,
                      fontSize: "0.875rem",
                      color: "#0f172a",
                      bgcolor: "#fff",
                      outline: "none",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography
                      sx={{ fontWeight: 600, minWidth: 48, textAlign: "center" }}
                    >
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>
    </HomeLayout>
  );
};

export default ClientManagement;
